import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, Trash2, X, Upload, Bold, Italic, ExternalLink } from 'lucide-react'
import { api, publicApi } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import { DeepLinkInput } from '../components/DeepLinkInput'
import CsvControls from '../components/CsvControls'

// ─── Config ────────────────────────────────────────────────────────────────────

const PLACEMENTS: Record<string, { label: string; description: string; aspect: string }> = {
  home_banner:           { label: 'Home Banner',          description: 'Full-width hero carousel at the top of the home screen', aspect: '4:1' },
  home_promo_card:       { label: 'Home Promo Card',      description: 'Square promotional card in the home screen feed',        aspect: '1:1' },
  browse_banner:         { label: 'Browse Banner',        description: 'Banner at the top of the Browse / Shop screen',          aspect: '4:1' },
  product_banner:        { label: 'Product Banner',       description: 'Card shown inside the product detail page',              aspect: '16:9' },
  cart_banner:           { label: 'Cart Banner',          description: 'Slim banner inside the shopping cart',                   aspect: '8:1' },
  popup:                 { label: 'Popup',                description: 'Modal overlay shown on app launch or page load',         aspect: '3:4' },
  section_new_arrivals:  { label: 'Section: New Arrivals', description: 'Slim banner above the New Arrivals shelf',             aspect: '5:1' },
  section_spirits:       { label: 'Section: Spirits',     description: 'Slim banner above the Spirits shelf',                   aspect: '5:1' },
  section_wine:          { label: 'Section: Wine',        description: 'Slim banner above the Wine shelf',                      aspect: '5:1' },
  section_beer:          { label: 'Section: Beer',        description: 'Slim banner above the Beer shelf',                      aspect: '5:1' },
  // Marketing site (drinx.jo) — auto-rotating carousel below the hero,
  // surfaces store / app campaigns. Multi-slide.
  web_hero:              { label: 'Website Carousel (desktop)', description: 'Desktop / tablet version of the auto-rotating campaign carousel on drinx.jo homepage. 21:9, wide cinematic.', aspect: '21:9' },
  web_hero_mobile:       { label: 'Website Carousel (mobile)',  description: 'Mobile (≤640px) version of the carousel. Paired with the desktop slide of matching sort order. Recommended 1200×1500.', aspect: '4:5' },
  // Marketing site — single page-top hero image. Sets brand tone,
  // rarely changes. Site overlays the tagline + CTAs on top.
  web_landing_hero:      { label: 'Website Landing Hero (desktop)', description: 'Desktop / tablet version of the page-top hero image. 21:9, leave the lower 40% relatively clean for the tagline + CTAs overlay.', aspect: '21:9' },
  web_landing_hero_mobile:{ label: 'Website Landing Hero (mobile)', description: 'Mobile (≤640px) version of the page-top hero. Recommended 1200×1500 (4:5 portrait). Leave lower third clean for the tagline overlay.', aspect: '4:5' },
}

const PLACEMENT_COLORS: Record<string, string> = {
  home_banner:           'bg-blue-500/15 text-blue-400',
  home_promo_card:       'bg-violet-500/15 text-violet-400',
  browse_banner:         'bg-cyan-500/15 text-cyan-400',
  product_banner:        'bg-teal-500/15 text-teal-400',
  cart_banner:           'bg-amber-500/15 text-amber-400',
  popup:                 'bg-pink-500/15 text-pink-400',
  section_new_arrivals:  'bg-orange-500/15 text-orange-400',
  section_spirits:       'bg-orange-500/15 text-orange-400',
  section_wine:          'bg-orange-500/15 text-orange-400',
  section_beer:          'bg-orange-500/15 text-orange-400',
  web_hero:                'bg-[#C41E3A]/15 text-[#C41E3A]',
  web_hero_mobile:         'bg-[#C41E3A]/15 text-[#C41E3A]',
  web_landing_hero:        'bg-[#C41E3A]/15 text-[#C41E3A]',
  web_landing_hero_mobile: 'bg-[#C41E3A]/15 text-[#C41E3A]',
}

const TAB_PLACEMENTS: Record<string, string[]> = {
  'Home':     ['home_banner', 'home_promo_card'],
  'Sections': ['section_new_arrivals', 'section_spirits', 'section_wine', 'section_beer'],
  'Other':    ['browse_banner', 'product_banner', 'cart_banner'],
  'Popups':   ['popup'],
  // Website tab: desktop variants first, mobile variants second.
  // Mobile variants pair by sort_order with the desktop variant
  // at the same position. See _shared/web-hero-pairing for detail.
  'Website':  ['web_landing_hero', 'web_landing_hero_mobile', 'web_hero', 'web_hero_mobile'],
}

// ─── Types ──────────────────────────────────────────────────────────────────────

interface Promotion {
  id: string; title: string; body: string | null; placement: string
  media_type: string; image_key: string | null; image_key_ar: string | null
  link_type: string; link_value: string | null
  show_title: number; sort_order: number; target: string
  popup_max_views: number
  starts_at: string | null; ends_at: string | null
  active: number; created_at: string; created_by_name: string | null
}

// ─── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  title:           z.string().min(1, 'Required'),
  body:            z.string().optional(),
  placement:       z.enum(['home_banner', 'home_promo_card', 'browse_banner', 'product_banner', 'cart_banner', 'popup',
                           'section_new_arrivals', 'section_spirits', 'section_wine', 'section_beer',
                           'web_hero', 'web_hero_mobile', 'web_landing_hero', 'web_landing_hero_mobile']),
  media_type:      z.enum(['image', 'gif', 'video']),
  image_key:       z.string().optional(),
  image_key_ar:    z.string().optional(),
  link_type:       z.enum(['none', 'in_app', 'external']),
  link_value:      z.string().optional(),
  show_title:      z.boolean(),
  sort_order:      z.number().int().min(0),
  target:          z.enum(['all', 'premium']),
  popup_max_views: z.number().int().min(0),
  starts_at:       z.string().optional(),
  ends_at:         z.string().optional(),
  active:          z.boolean(),
})
type FormData = z.infer<typeof schema>

// ─── Date helpers ───────────────────────────────────────────────────────────────

// Convert a UTC ISO string (or bare datetime-local string from legacy data) to
// the datetime-local input format in the browser's local timezone.
function utcToLocal(s: string | null): string {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return s.slice(0, 16)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Convert a datetime-local string (local time) to a UTC ISO string for storage.
function localToUtc(s: string): string | null {
  if (!s) return null
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

// ─── Media thumbnail ────────────────────────────────────────────────────────────

function MediaThumb({ imageKey, mediaType }: { imageKey: string | null; mediaType: string }) {
  if (!imageKey) return null
  const url = `https://files.drinx.jo/${imageKey}`
  return mediaType === 'video'
    ? <video src={url} className="w-16 h-10 object-cover rounded-lg shrink-0" muted />
    : <img src={url} alt="" className="w-16 h-10 object-cover rounded-lg shrink-0" />
}

// ─── List ───────────────────────────────────────────────────────────────────────

interface PromoShelfItem {
  id: string; product_id: string; promo_price_fils: number; sort_order: number; active: number
  name_en: string; name_ar: string; price: number; image_key: string | null; sku: string; brand: string | null
}

const TABS = ['Home', 'Sections', 'Other', 'Popups', 'Website', 'Promo Shelf'] as const
type Tab = typeof TABS[number]

export default function Promotions() {
  const { can } = useAuth()
  const [panel, setPanel] = useState<Promotion | null | 'new'>(null)
  const [activeTab, setActiveTab] = useState<Tab>('Home')
  const [shelfPanel, setShelfPanel] = useState(false)
  const qc = useQueryClient()

  const { data } = useQuery<{ promotions: Promotion[] }>({
    queryKey: ['promotions'],
    queryFn:  () => api.get('/promotions').then(r => r.data),
  })

  const { data: shelfData, refetch: refetchShelf } = useQuery<{ items: PromoShelfItem[] }>({
    queryKey: ['promo-shelf'],
    queryFn:  () => api.get('/promo-shelf').then(r => r.data),
    enabled: activeTab === 'Promo Shelf',
  })

  const deletePromo = useMutation({
    mutationFn: (id: string) => api.delete(`/promotions/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  })

  const deleteShelfItem = useMutation({
    mutationFn: (id: string) => api.delete(`/promo-shelf/${id}`),
    onSuccess:  () => refetchShelf(),
  })

  const canEdit = can(['super_admin', 'operations', 'marketing'])

  const grouped: Record<string, Promotion[]> = {}
  for (const p of data?.promotions ?? []) {
    ;(grouped[p.placement] ??= []).push(p)
  }

  const tabPlacements = activeTab !== 'Promo Shelf' ? (TAB_PLACEMENTS[activeTab] ?? []) : []
  const defaultPlacement = tabPlacements[0] as FormData['placement'] | undefined

  function formatFils(fils: number) {
    return `JD ${(fils / 1000).toFixed(3)}`
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Promotions</h1>
        {canEdit && activeTab !== 'Promo Shelf' && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/promotions"
              templateFilename="promotions-template.csv"
              exportFilename={`promotions-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="promotion"
              onImported={() => qc.invalidateQueries({ queryKey: ['promotions'] })}
            />
            <button
              onClick={() => setPanel('new')}
              className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={15} /> New Promotion
            </button>
          </div>
        )}
        {canEdit && activeTab === 'Promo Shelf' && (
          <button
            onClick={() => setShelfPanel(true)}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={15} /> Add Product
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#2e2e2e]">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-[#C41E3A] text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab}
            {tab !== 'Promo Shelf' && (TAB_PLACEMENTS[tab] ?? []).reduce((n, p) => n + (grouped[p]?.length ?? 0), 0) > 0 && (
              <span className="ml-1.5 text-xs bg-[#2e2e2e] text-gray-400 px-1.5 py-0.5 rounded-full">
                {(TAB_PLACEMENTS[tab] ?? []).reduce((n, p) => n + (grouped[p]?.length ?? 0), 0)}
              </span>
            )}
            {tab === 'Promo Shelf' && (shelfData?.items?.length ?? 0) > 0 && (
              <span className="ml-1.5 text-xs bg-[#2e2e2e] text-gray-400 px-1.5 py-0.5 rounded-full">
                {shelfData!.items.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Promo Shelf tab */}
      {activeTab === 'Promo Shelf' && (
        <div className="space-y-2">
          {shelfData?.items.length === 0 && (
            <div className="bg-[#1a1a1a] border border-dashed border-[#2e2e2e] rounded-xl px-4 py-6 text-xs text-gray-700 text-center">
              No products on the promo shelf yet — add some deals
            </div>
          )}
          {shelfData?.items.map(item => {
            const pct = item.price > 0 ? Math.round((item.price - item.promo_price_fils) / item.price * 100) : 0
            return (
              <div key={item.id} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-3 flex items-center gap-4">
                {item.image_key && (
                  <img src={`https://files.drinx.jo/${item.image_key}`} alt=""
                    className="w-12 h-12 object-cover rounded-lg shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{item.name_en}</p>
                  {item.brand && <p className="text-gray-600 text-xs">{item.brand}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-gray-600 text-xs line-through">{formatFils(item.price)}</span>
                    <span className="text-[#C41E3A] text-sm font-semibold">{formatFils(item.promo_price_fils)}</span>
                    {pct > 0 && (
                      <span className="bg-[#C41E3A]/15 text-[#C41E3A] text-xs px-1.5 py-0.5 rounded-full font-medium">
                        −{pct}%
                      </span>
                    )}
                    {!item.active && (
                      <span className="bg-gray-500/15 text-gray-500 text-xs px-1.5 py-0.5 rounded-full">Inactive</span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={() => { if (confirm('Remove from promo shelf?')) deleteShelfItem.mutate(item.id) }}
                    className="text-gray-500 hover:text-red-400 transition-colors p-1.5 shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Promotions tabs */}
      {activeTab !== 'Promo Shelf' && tabPlacements.map(placementKey => {
        const items = grouped[placementKey]
        if (!items?.length && !canEdit) return null
        return (
          <div key={placementKey} className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLACEMENT_COLORS[placementKey]}`}>
                {PLACEMENTS[placementKey].label}
              </span>
              <span className="text-xs text-gray-600">{PLACEMENTS[placementKey].description}</span>
              <span className="text-xs text-gray-700 ml-auto">aspect {PLACEMENTS[placementKey].aspect}</span>
            </div>

            {items?.length ? (
              <div className="space-y-2">
                {items.map(p => (
                  <div key={p.id} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-3 flex items-start gap-4">
                    <MediaThumb imageKey={p.image_key} mediaType={p.media_type} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        {!p.active && (
                          <span className="text-xs bg-gray-500/15 text-gray-500 px-2 py-0.5 rounded-full">Inactive</span>
                        )}
                        <span className="text-xs text-gray-600 capitalize">{p.target === 'all' ? 'All users' : 'Premium only'}</span>
                        {p.link_type !== 'none' && (
                          <span className="text-xs text-gray-600 flex items-center gap-1">
                            <ExternalLink size={10} />
                            {p.link_type === 'in_app' ? 'In-app link' : 'External URL'}
                          </span>
                        )}
                      </div>
                      <p className="text-white font-medium text-sm">{p.title}</p>
                      {p.body && <p className="text-gray-500 text-xs mt-0.5 line-clamp-2">{p.body}</p>}
                      <div className="flex gap-4 mt-1.5 text-xs text-gray-600 flex-wrap">
                        {p.sort_order > 0 && <span>Order #{p.sort_order}</span>}
                        {p.placement === 'popup' && (
                          <span>{p.popup_max_views === 0 ? 'Always shown' : `Shows ${p.popup_max_views}×`}</span>
                        )}
                        {p.starts_at && <span>From {formatDateTime(p.starts_at)}</span>}
                        {p.ends_at   && <span>Until {formatDateTime(p.ends_at)}</span>}
                        {p.link_value && (
                          <span className="font-mono truncate max-w-[200px]">{p.link_value}</span>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setPanel(p)} className="text-gray-500 hover:text-white transition-colors p-1.5">
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => { if (confirm('Delete this promotion?')) deletePromo.mutate(p.id) }}
                          className="text-gray-500 hover:text-red-400 transition-colors p-1.5"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#1a1a1a] border border-dashed border-[#2e2e2e] rounded-xl px-4 py-3 text-xs text-gray-700 text-center">
                No promotions in this slot
              </div>
            )}
          </div>
        )
      })}

      {panel !== null && (
        <PromotionPanel
          promotion={panel === 'new' ? null : panel}
          defaultPlacement={panel === 'new' ? defaultPlacement : undefined}
          onClose={() => setPanel(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['promotions'] }); setPanel(null) }}
        />
      )}

      {shelfPanel && (
        <PromoShelfAddPanel
          onClose={() => setShelfPanel(false)}
          onSaved={() => { refetchShelf(); setShelfPanel(false) }}
        />
      )}
    </div>
  )
}

// ─── Promo Shelf Add Panel ────────────────────────────────────────────────────

function PromoShelfAddPanel({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [search, setSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selected, setSelected] = useState<{ id: string; name_en: string; price: number } | null>(null)
  const [promoPrice, setPromoPrice] = useState('')
  const [sortOrder, setSortOrder] = useState('0')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const { data: searchData } = useQuery<{ products: { id: string; name_en: string; price: number; brand: string | null }[] }>({
    queryKey: ['product-search', search],
    queryFn:  () => publicApi.get(`/products?search=${encodeURIComponent(search)}&limit=8`).then(r => r.data),
    enabled:  search.length > 1 && showDropdown,
  })

  async function handleSave() {
    if (!selected) { setErr('Select a product'); return }
    const fils = Math.round(parseFloat(promoPrice) * 1000)
    if (!fils || fils <= 0) { setErr('Enter a valid promo price in JOD (e.g. 12.500)'); return }
    setSaving(true); setErr('')
    try {
      await api.post('/promo-shelf', { product_id: selected.id, promo_price_fils: fils, sort_order: parseInt(sortOrder) || 0 })
      onSaved()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      setErr(msg ?? 'Failed to save')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#111] border-l border-[#2e2e2e] h-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Add to Promo Shelf</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Search product</label>
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setShowDropdown(true); if (selected) setSelected(null) }}
              placeholder="Type product name…"
              className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
            {showDropdown && searchData?.products.length ? (
              <div className="mt-1 border border-[#2e2e2e] rounded-lg overflow-hidden">
                {searchData.products.map(p => (
                  <button key={p.id} onClick={() => { setSelected(p); setSearch(p.name_en); setShowDropdown(false) }}
                    className="w-full text-left px-3 py-2 text-sm transition-colors flex justify-between text-white hover:bg-[#2e2e2e]">
                    <span>{p.name_en}{p.brand ? ` — ${p.brand}` : ''}</span>
                    <span className="text-gray-500 text-xs shrink-0 ml-2">JD {(p.price / 1000).toFixed(3)}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {selected && (
            <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-xs text-gray-400">
              Regular price: <span className="text-white font-medium">JD {(selected.price / 1000).toFixed(3)}</span>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Promo price (JOD, e.g. 12.500)</label>
            <input value={promoPrice} onChange={e => setPromoPrice(e.target.value)} placeholder="0.000" type="number" step="0.001"
              className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
            {selected && promoPrice && (
              <p className="text-xs text-[#C41E3A] mt-1">
                −{Math.round((selected.price - parseFloat(promoPrice) * 1000) / selected.price * 100)}% discount
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Sort order (0 = first)</label>
            <input value={sortOrder} onChange={e => setSortOrder(e.target.value)} type="number" min={0}
              className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
          </div>

          {err && <p className="text-red-400 text-xs">{err}</p>}
        </div>
        <div className="border-t border-[#2e2e2e] px-5 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
            {saving ? 'Adding…' : 'Add to shelf'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Form panel ─────────────────────────────────────────────────────────────────

function PromotionPanel({ promotion, defaultPlacement, onClose, onSaved }: {
  promotion: Promotion | null; defaultPlacement?: FormData['placement']; onClose: () => void; onSaved: () => void
}) {
  const { register, handleSubmit, control, watch, setValue,
          formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: promotion ? {
      title:           promotion.title,
      body:            promotion.body ?? '',
      placement:       promotion.placement as FormData['placement'],
      media_type:      (promotion.media_type ?? 'image') as FormData['media_type'],
      image_key:       promotion.image_key ?? '',
      image_key_ar:    promotion.image_key_ar ?? '',
      link_type:       (promotion.link_type ?? 'none') as FormData['link_type'],
      link_value:      promotion.link_value ?? '',
      show_title:      promotion.show_title !== 0,
      sort_order:      promotion.sort_order ?? 0,
      target:          (promotion.target ?? 'all') as FormData['target'],
      popup_max_views: promotion.popup_max_views ?? 1,
      starts_at:       utcToLocal(promotion.starts_at),
      ends_at:         utcToLocal(promotion.ends_at),
      active:          !!promotion.active,
    } : {
      placement: defaultPlacement ?? 'home_banner', media_type: 'image', link_type: 'none',
      show_title: true, sort_order: 0, target: 'all', popup_max_views: 1, active: true,
    },
  })

  const linkType    = watch('link_type')
  const imageKey    = watch('image_key')
  const imageKeyAr  = watch('image_key_ar')
  const mediaType   = watch('media_type')
  const bodyValue   = watch('body') ?? ''
  const placement   = watch('placement')

  const [uploading, setUploading] = useState(false)
  const [uploadingAr, setUploadingAr] = useState(false)
  const [uploadErr, setUploadErr] = useState('')
  const [uploadErrAr, setUploadErrAr] = useState('')
  const [showArVariant, setShowArVariant] = useState(false)
  const [saveErr, setSaveErr]     = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const fileArRef = useRef<HTMLInputElement>(null)

  // Auto-open the Arabic variant pane when editing a promotion that
  // already has one — saves the operator a discovery click.
  useEffect(() => {
    if (imageKeyAr) setShowArVariant(true)
  }, [imageKeyAr])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadErr('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/promotions/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setValue('image_key', res.data.key)
      setValue('media_type', res.data.media_type)
    } catch {
      setUploadErr('Upload failed. Try again.')
    } finally {
      setUploading(false)
    }
  }

  // Optional Arabic variant — uploaded separately, picked by the site
  // when locale === 'ar' AND a value is present. Used for text-bearing
  // banners (campaign copy baked into the image); pure photography
  // doesn't need this and can leave it blank.
  async function handleArFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingAr(true)
    setUploadErrAr('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/promotions/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setValue('image_key_ar', res.data.key)
    } catch {
      setUploadErrAr('Upload failed. Try again.')
    } finally {
      setUploadingAr(false)
    }
  }

  function insertMarkdown(syntax: 'bold' | 'italic') {
    const textarea = document.getElementById('promo-body') as HTMLTextAreaElement | null
    if (!textarea) return
    const start = textarea.selectionStart
    const end   = textarea.selectionEnd
    const sel   = bodyValue.slice(start, end) || 'text'
    const wrap  = syntax === 'bold' ? `**${sel}**` : `_${sel}_`
    const next  = bodyValue.slice(0, start) + wrap + bodyValue.slice(end)
    setValue('body', next)
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + (syntax === 'bold' ? 2 : 1), start + wrap.length - (syntax === 'bold' ? 2 : 1))
    }, 0)
  }

  async function onSubmit(data: FormData) {
    setSaveErr('')
    try {
      const payload = {
        ...data,
        body:          data.body          || null,
        image_key:     data.image_key     || null,
        image_key_ar:  data.image_key_ar  || null,
        link_value:    data.link_type === 'none' ? null : (data.link_value || null),
        starts_at:     localToUtc(data.starts_at ?? ''),
        ends_at:       localToUtc(data.ends_at ?? ''),
      }
      if (promotion) {
        await api.put(`/promotions/${promotion.id}`, payload)
      } else {
        await api.post('/promotions', payload)
      }
      onSaved()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setSaveErr(msg ?? 'Failed to save. Please try again.')
    }
  }

  const previewUrl = imageKey ? `https://files.drinx.jo/${imageKey}` : null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#111] border-l border-[#2e2e2e] h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e] shrink-0">
          <h2 className="text-sm font-semibold text-white">
            {promotion ? 'Edit Promotion' : 'New Promotion'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-6">

            {/* ── Placement ── */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Placement</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(PLACEMENTS).map(([key, cfg]) => {
                  const isActive = watch('placement') === key
                  return (
                    <label key={key} className={`flex flex-col gap-0.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                      isActive ? 'border-[#C41E3A] bg-[#C41E3A]/8' : 'border-[#2e2e2e] hover:border-[#444]'
                    }`}>
                      <input type="radio" {...register('placement')} value={key} className="sr-only" />
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${isActive ? 'text-[#C41E3A]' : 'text-white'}`}>{cfg.label}</span>
                        <span className="text-[10px] text-gray-600 ml-auto">{cfg.aspect}</span>
                      </div>
                      <p className="text-[11px] text-gray-600 leading-tight">{cfg.description}</p>
                    </label>
                  )
                })}
              </div>
            </section>

            {/* ── Media ── */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Media</p>
              <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime"
                className="hidden" onChange={handleFileChange} />

              {previewUrl ? (
                <div className="relative rounded-xl overflow-hidden bg-[#1a1a1a] border border-[#2e2e2e]">
                  {mediaType === 'video'
                    ? <video src={previewUrl} className="w-full max-h-48 object-contain" controls muted />
                    : <img src={previewUrl} alt="" className="w-full max-h-48 object-contain" />
                  }
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="bg-black/70 hover:bg-black/90 text-white text-xs px-2.5 py-1 rounded-lg transition-colors">
                      Replace
                    </button>
                    <button type="button" onClick={() => { setValue('image_key', ''); setValue('media_type', 'image') }}
                      className="bg-black/70 hover:bg-red-900/80 text-white p-1 rounded-lg transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                  <div className="absolute bottom-2 left-2">
                    <span className="bg-black/70 text-white text-[10px] px-2 py-0.5 rounded capitalize">{mediaType}</span>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full flex flex-col items-center gap-2 border border-dashed border-[#3a3a3a] hover:border-[#C41E3A]/50 rounded-xl py-8 transition-colors text-gray-600 hover:text-gray-400 disabled:opacity-50">
                  <Upload size={20} />
                  <span className="text-sm">{uploading ? 'Uploading…' : 'Upload image, GIF, or video'}</span>
                  <span className="text-xs text-gray-700">JPG, PNG, WebP, GIF, MP4, WebM</span>
                </button>
              )}
              {uploadErr && <p className="text-red-400 text-xs mt-1.5">{uploadErr}</p>}

              {/* Optional Arabic variant. Only relevant for placements
                  where text gets baked into the image; pure photography
                  reuses `image_key` for both locales. */}
              <div className="mt-3">
                {!showArVariant && !imageKeyAr ? (
                  <button
                    type="button"
                    onClick={() => setShowArVariant(true)}
                    className="text-[11px] text-gray-500 hover:text-[#C41E3A] underline-offset-2 hover:underline"
                  >
                    + Add Arabic variant (for text-bearing banners)
                  </button>
                ) : (
                  <div className="space-y-2 pt-3 border-t border-[#2e2e2e]">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600 uppercase tracking-wider">Arabic variant <span className="normal-case text-gray-700">(optional)</span></p>
                      {!imageKeyAr && (
                        <button
                          type="button"
                          onClick={() => setShowArVariant(false)}
                          className="text-[11px] text-gray-600 hover:text-gray-400"
                        >
                          Hide
                        </button>
                      )}
                    </div>
                    <input ref={fileArRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime"
                      className="hidden" onChange={handleArFileChange} />
                    {imageKeyAr ? (
                      <div className="relative rounded-xl overflow-hidden bg-[#1a1a1a] border border-[#2e2e2e]">
                        <img src={`https://files.drinx.jo/${imageKeyAr}`} alt="" dir="rtl"
                          className="w-full max-h-48 object-contain" />
                        <div className="absolute top-2 right-2 flex gap-1.5">
                          <button type="button" onClick={() => fileArRef.current?.click()}
                            className="bg-black/70 hover:bg-black/90 text-white text-xs px-2.5 py-1 rounded-lg transition-colors">
                            Replace
                          </button>
                          <button type="button" onClick={() => setValue('image_key_ar', '')}
                            className="bg-black/70 hover:bg-red-900/80 text-white p-1 rounded-lg transition-colors">
                            <X size={12} />
                          </button>
                        </div>
                        <div className="absolute bottom-2 left-2">
                          <span className="bg-black/70 text-white text-[10px] px-2 py-0.5 rounded">العربية</span>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileArRef.current?.click()} disabled={uploadingAr}
                        className="w-full flex flex-col items-center gap-2 border border-dashed border-[#3a3a3a] hover:border-[#C41E3A]/50 rounded-xl py-6 transition-colors text-gray-600 hover:text-gray-400 disabled:opacity-50">
                        <Upload size={18} />
                        <span className="text-xs">{uploadingAr ? 'Uploading…' : 'Upload Arabic-text variant'}</span>
                      </button>
                    )}
                    {uploadErrAr && <p className="text-red-400 text-xs">{uploadErrAr}</p>}
                    <p className="text-[11px] text-gray-600 leading-relaxed">
                      Shown to visitors on the Arabic version of the site. Leave blank if the main
                      image has no English text — the same photo will serve both locales.
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* ── Content ── */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Content</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Title</label>
                  <input {...register('title')}
                    className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
                  {errors.title && <p className="text-xs text-red-400 mt-1">{errors.title.message}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs text-gray-400">Body text (optional, supports **bold** and _italic_)</label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => insertMarkdown('bold')}
                        className="p-1 text-gray-600 hover:text-white hover:bg-[#2e2e2e] rounded transition-colors" title="Bold">
                        <Bold size={13} />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('italic')}
                        className="p-1 text-gray-600 hover:text-white hover:bg-[#2e2e2e] rounded transition-colors" title="Italic">
                        <Italic size={13} />
                      </button>
                    </div>
                  </div>
                  <textarea id="promo-body" {...register('body')} rows={3}
                    className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none font-mono" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input {...register('show_title')} type="checkbox" className="w-4 h-4 accent-[#C41E3A]" />
                  <span className="text-sm text-gray-300">Show title overlay on image</span>
                  <span className="text-xs text-gray-600 ml-1">(uncheck to hide — useful when the image already contains text)</span>
                </label>
              </div>
            </section>

            {/* ── Link ── */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Link</p>
              <div className="flex gap-2 mb-3">
                {(['none', 'in_app', 'external'] as const).map(lt => (
                  <label key={lt} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border cursor-pointer text-xs transition-colors ${
                    linkType === lt ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-[#C41E3A]' : 'border-[#2e2e2e] text-gray-500 hover:border-[#444]'
                  }`}>
                    <input type="radio" {...register('link_type')} value={lt} className="sr-only" />
                    {lt === 'none' ? 'No link' : lt === 'in_app' ? 'In-app' : 'External URL'}
                  </label>
                ))}
              </div>

              {linkType === 'in_app' && (
                <Controller
                  name="link_value"
                  control={control}
                  render={({ field }) => (
                    <DeepLinkInput value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
              )}

              {linkType === 'external' && (
                <input {...register('link_value')} placeholder="https://example.com/promo"
                  className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#C41E3A]" />
              )}
            </section>

            {/* ── Schedule & Audience ── */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Schedule & Audience</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Starts at</label>
                    <input {...register('starts_at')} type="datetime-local"
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Ends at</label>
                    <input {...register('ends_at')} type="datetime-local"
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Audience</label>
                    <select {...register('target')}
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
                      <option value="all">All users</option>
                      <option value="premium">Premium only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Sort order</label>
                    <input {...register('sort_order', { valueAsNumber: true })} type="number" min={0}
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
                    <p className="text-[10px] text-gray-600 mt-1">Lower = shown first in slot</p>
                  </div>
                </div>
                {placement === 'popup' && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Max times shown per device</label>
                    <input
                      {...register('popup_max_views', { valueAsNumber: true })}
                      type="number" min={0}
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
                    />
                    <p className="text-[10px] text-gray-600 mt-1">
                      0 = always show (unlimited) · 1 = once · 2 = twice, etc.
                    </p>
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input {...register('active')} type="checkbox" className="w-4 h-4 accent-[#C41E3A]" />
                  <span className="text-sm text-gray-300">Active</span>
                </label>
              </div>
            </section>
          </div>

          {/* Footer */}
          {saveErr && (
            <div className="mx-5 mb-0 text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">{saveErr}</div>
          )}
          <div className="border-t border-[#2e2e2e] px-5 py-4 flex justify-end gap-3 shrink-0 bg-[#111]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}
              className="px-5 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
              {isSubmitting ? 'Saving…' : promotion ? 'Save changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
